import apiClient from '@/lib/api-client';
import type { Envelope } from '@/types/quotes';
import type { QuickbooksAccount, QuickbooksCallbackBody, QuickbooksSettingsPatch, QuickbooksState, QuickbooksTaxCode } from '@/types/quickbooks';
import { ADMIN, unwrap } from './quotes-http';

/** The business's QuickBooks connection (owner only; 404 while the `quickbooks` flag is off, 501 where Intuit is not set up). Push only. */
export const quickbooksService = {
  async status(): Promise<QuickbooksState> {
    return unwrap(await apiClient.get<Envelope<QuickbooksState>>(`${ADMIN}/quickbooks`));
  },
  /** The Intuit sign-in link: open it in the same tab. */
  async connect(): Promise<{ authorize_url: string; expires_in: number }> {
    return unwrap(await apiClient.post<Envelope<{ authorize_url: string; expires_in: number }>>(`${ADMIN}/quickbooks/connect`));
  },
  /** What Intuit sent back to the callback page. The code works once, so this is never retried. */
  async callback(body: QuickbooksCallbackBody): Promise<QuickbooksState> {
    return unwrap(await apiClient.post<Envelope<QuickbooksState>>(`${ADMIN}/quickbooks/callback`, body));
  },
  /** The company's active accounts for the Choose accounts screen; `truncated` when it has more than the 5,000 read. */
  async accounts(): Promise<{ accounts: QuickbooksAccount[]; truncated: boolean }> {
    return unwrap(await apiClient.get<Envelope<{ accounts: QuickbooksAccount[]; truncated: boolean }>>(`${ADMIN}/quickbooks/accounts`));
  },
  async taxCodes(): Promise<QuickbooksTaxCode[]> {
    return unwrap(await apiClient.get<Envelope<{ tax_codes: QuickbooksTaxCode[] }>>(`${ADMIN}/quickbooks/tax_codes`)).tax_codes;
  },
  /** Only the keys in the patch change. Answers the new status. */
  async saveSettings(patch: QuickbooksSettingsPatch): Promise<QuickbooksState> {
    return unwrap(await apiClient.put<Envelope<QuickbooksState>>(`${ADMIN}/quickbooks/settings`, patch));
  },
  async disconnect(): Promise<QuickbooksState> {
    return unwrap(await apiClient.post<Envelope<QuickbooksState>>(`${ADMIN}/quickbooks/disconnect`));
  },
};
